/**
 * Cookie consent banner — POPIA s11 + GDPR Art. 7 affirmative opt-in.
 *
 * Renders a fixed-bottom drawer with three categories (necessary on +
 * locked; analytics + marketing default OFF). The user can:
 *
 *   - "Accept all" — flips analytics + marketing on, persists.
 *   - "Decline non-essential" — keeps analytics + marketing off, persists.
 *   - "Customize" — opens the per-category toggle pane (in-banner, no
 *     modal — Clio Manage / NetDocuments precedent).
 *
 * The banner respects DNT: if `respectsDnt()` returns true the toggles
 * stay off and a small notice tells the visitor we honoured the header.
 * The visitor can still flip them on — DNT is a preference, not a lock.
 *
 * Banner does NOT show when `getConsent()` returns a non-null envelope
 * for the current POLICY_VERSION. When the policy version bumps in the
 * future, every existing decision becomes stale and the banner re-shows.
 *
 * Backend persistence: on submit, if the user is authenticated we POST
 * `/v1/me/consent` (handler at backend/src/handlers/me-consent-post.ts).
 * On non-auth visits we only persist client-side.
 *
 * Light theme is the product default per UX spec §2.3 — the banner uses
 * --surface / --ink tokens so it picks up the right palette without
 * special-casing.
 */

import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import {
  defaultChoices,
  getConsent,
  postConsentToBackend,
  respectsDnt,
  setConsent,
  shouldShowBanner,
  type ConsentChoices,
} from '../../lib/cookie-consent';
import { supabase } from '../../lib/supabase';

interface CookieConsentBannerProps {
  /** Test seam — defaults to the live `getConsent`. */
  initialConsent?: ReturnType<typeof getConsent>;
  /** Test seam — defaults to `respectsDnt()`. */
  detectDnt?: () => boolean;
  /** Test seam — defaults to the live fetcher in postConsentToBackend. */
  postFn?: typeof postConsentToBackend;
  /** Test seam — return the current access token. Defaults to a Supabase read. */
  tokenFn?: () => Promise<string | null>;
}

export function CookieConsentBanner({
  initialConsent,
  detectDnt = respectsDnt,
  postFn = postConsentToBackend,
  tokenFn,
}: CookieConsentBannerProps = {}) {
  const [visible, setVisible] = useState<boolean>(
    initialConsent === undefined
      ? false /* hydrate-after-mount to avoid SSR flash */
      : shouldShowBanner(initialConsent),
  );
  const [customising, setCustomising] = useState(false);
  const [choices, setChoices] = useState<Omit<ConsentChoices, 'necessary'>>(
    defaultChoices(),
  );
  const [dntDetected, setDntDetected] = useState(false);

  // Hydrate from localStorage on mount — keeps SSR-safe render path even
  // though TanStack Router renders client-side today, so we don't paint
  // the banner when consent is already stored.
  useEffect(() => {
    if (initialConsent !== undefined) return;
    const stored = getConsent();
    setVisible(shouldShowBanner(stored));
    setDntDetected(detectDnt());
  }, [initialConsent, detectDnt]);

  if (!visible) return null;

  async function commit(next: Omit<ConsentChoices, 'necessary'>) {
    const envelope = setConsent(next, { viaDnt: dntDetected });
    setVisible(false);
    setCustomising(false);
    // Fire-and-forget backend write — don't block the banner dismiss on it.
    const token = await (tokenFn ? tokenFn() : readSupabaseAccessToken());
    void postFn(envelope, token);
  }

  const acceptAll = () => commit({ analytics: true, marketing: true });
  const declineAll = () => commit({ analytics: false, marketing: false });
  const submitCustom = () => commit(choices);

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-body"
      data-testid="cookie-consent-banner"
      style={{
        position: 'fixed',
        left: 16,
        right: 16,
        bottom: 16,
        zIndex: 60,
        maxWidth: 720,
        margin: '0 auto',
        background: 'var(--surface)',
        color: 'var(--ink)',
        border: '1px solid var(--hairline)',
        borderRadius: 14,
        boxShadow: '0 16px 48px rgba(20,18,12,0.18)',
        padding: '20px 22px 18px',
        fontFamily: 'var(--sans)',
        // Bug #2 (2026-08-13 dev walk): aria-modal="false" here is deliberate
        // (see file docstring — non-modal, Clio/NetDocuments precedent), but a
        // plain `position: fixed` card still captured pointer events across
        // its whole box, including padding and gaps between controls. On the
        // onboarding subscription step that box lands over "Subscribe &
        // Continue" and swallows the click (Playwright: 20s retry against
        // this dialog intercepting pointer events). Since this banner stays
        // non-modal by design, the fix is the OTHER option in the brief: the
        // card itself no longer accepts pointer events, and only the actual
        // controls inside it (links, buttons, checkboxes) opt back in below.
        // Keyboard/focus/Tab behaviour is untouched — pointer-events has no
        // effect on focus order.
        pointerEvents: 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
        <div>
          <h2
            id="cookie-consent-title"
            className="serif"
            style={{
              fontSize: 18,
              fontStyle: 'italic',
              fontWeight: 400,
              margin: 0,
              color: 'var(--ink)',
            }}
          >
            Cookies that hold the trail
          </h2>
          <p
            id="cookie-consent-body"
            style={{
              fontSize: 13.5,
              color: 'var(--ink-2)',
              margin: '6px 0 0',
              lineHeight: 1.5,
            }}
          >
            We use essential cookies to keep you signed in and to remember your
            tenant. Analytics and marketing cookies are off by default. You can
            change this any time on our{' '}
            <Link
              to="/legal/cookies"
              style={{ color: 'var(--primary-600)', textDecoration: 'underline', pointerEvents: 'auto' }}
            >
              cookie policy page
            </Link>
            .
          </p>
          {dntDetected && (
            <p
              data-testid="cookie-consent-dnt-notice"
              style={{
                fontSize: 12,
                color: 'var(--ink-3)',
                margin: '6px 0 0',
              }}
            >
              We detected a Do Not Track signal and pre-set your choices to
              decline. You can still opt in below if you change your mind.
            </p>
          )}
        </div>
      </div>

      {customising && (
        <div
          data-testid="cookie-consent-customise-pane"
          style={{
            border: '1px solid var(--hairline)',
            borderRadius: 10,
            padding: '14px 16px',
            marginBottom: 14,
            background: 'var(--bg)',
          }}
        >
          <ToggleRow
            label="Strictly necessary"
            description="Session, CSRF, and tenant routing. Always on: the product cannot work without these."
            checked
            disabled
            onChange={() => undefined}
            testId="cookie-toggle-necessary"
          />
          <ToggleRow
            label="Analytics"
            // 2026-08-05: this previously read "Stored in PostHog (EU)". Elreign does
            // not use PostHog — it appears nowhere in the codebase — and the privacy
            // notice says analytics, if and when we ship them, will be self-hosted in
            // South Africa. Naming a processor we do not use, in the wrong country, is
            // a false statement about personal-data processing. Do not name a processor
            // here until one is actually live and listed at /legal/subprocessors.
            description="Aggregated, pseudonymised usage data so we can fix what's slow or broken. Not currently collected. When we do, it will be self-hosted in South Africa and listed under our sub-processors."
            checked={choices.analytics}
            disabled={false}
            onChange={(v) => setChoices((c) => ({ ...c, analytics: v }))}
            testId="cookie-toggle-analytics"
          />
          <ToggleRow
            label="Marketing"
            description="Attribution for sales conversations (which ad / referral brought you here). Off by default."
            checked={choices.marketing}
            disabled={false}
            onChange={(v) => setChoices((c) => ({ ...c, marketing: v }))}
            testId="cookie-toggle-marketing"
          />
        </div>
      )}

      <div
        style={{
          display: 'flex',
          gap: 10,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {!customising ? (
          <>
            <button
              type="button"
              onClick={() => setCustomising(true)}
              data-testid="cookie-consent-customise"
              style={{
                background: 'transparent',
                border: '1px solid var(--hairline)',
                padding: '9px 16px',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--ink-2)',
                cursor: 'pointer',
                fontWeight: 500,
                pointerEvents: 'auto',
              }}
            >
              Customize
            </button>
            <button
              type="button"
              onClick={declineAll}
              data-testid="cookie-consent-decline"
              style={{
                background: 'transparent',
                border: '1px solid var(--hairline)',
                padding: '9px 16px',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--ink-2)',
                cursor: 'pointer',
                fontWeight: 500,
                pointerEvents: 'auto',
              }}
            >
              Decline non-essential
            </button>
            <button
              type="button"
              onClick={acceptAll}
              data-testid="cookie-consent-accept-all"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '9px 18px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              Accept all
            </button>
          </>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setCustomising(false)}
              data-testid="cookie-consent-customise-back"
              style={{
                background: 'transparent',
                border: '1px solid var(--hairline)',
                padding: '9px 16px',
                borderRadius: 8,
                fontSize: 13,
                color: 'var(--ink-2)',
                cursor: 'pointer',
                fontWeight: 500,
                pointerEvents: 'auto',
              }}
            >
              Back
            </button>
            <button
              type="button"
              onClick={submitCustom}
              data-testid="cookie-consent-save"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '9px 18px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                pointerEvents: 'auto',
              }}
            >
              Save my choices
            </button>
          </>
        )}
      </div>
    </div>
  );
}

interface ToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (value: boolean) => void;
  testId: string;
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
  testId,
}: ToggleRowProps) {
  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 14,
        padding: '10px 0',
        borderBottom: '1px solid var(--hairline)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        // The whole banner is pointer-events:none by default (bug #2) — this
        // row holds a real checkbox, so it opts back in. Children with no
        // pointer-events of their own (the checkbox input) inherit 'auto'
        // from here.
        pointerEvents: 'auto',
      }}
    >
      <span>
        <span
          style={{
            display: 'block',
            fontSize: 13.5,
            fontWeight: 600,
            color: 'var(--ink)',
            marginBottom: 2,
          }}
        >
          {label}
        </span>
        <span
          style={{
            display: 'block',
            fontSize: 12.5,
            color: 'var(--ink-3)',
            lineHeight: 1.45,
          }}
        >
          {description}
        </span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        style={{
          width: 18,
          height: 18,
          accentColor: 'var(--primary-700)',
          marginTop: 2,
          cursor: disabled ? 'not-allowed' : 'pointer',
        }}
      />
    </label>
  );
}

/**
 * Read the current Supabase access token without throwing if Supabase
 * is not initialised (e.g. in unit tests that mock the import).
 */
async function readSupabaseAccessToken(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token ?? null;
  } catch {
    return null;
  }
}
